import type {
  CreateCustomerTeamMemberInput,
  CustomerTeamMember,
  PatchCustomerTeamMemberInput,
} from '@clockwise/clients/customer-team';
import { ApiError, isoDateString } from '@clockwise/shared';
import { DatePicker } from '@clockwise/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { useUnsavedChangesGuard } from '@/hooks/use-unsaved-changes-guard';
import { customerTeamApi } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { localTodayIso } from '@/lib/utils';
import { ConfirmDialog } from './confirm-dialog';
import { ServiceRoleAutocomplete } from './service-role-autocomplete';
import { UserAutocomplete } from './user-autocomplete';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customerId: string;
  /** When set, the sheet is in edit mode for this membership. */
  member?: CustomerTeamMember | null;
}

const FormSchema = z
  .object({
    userId: z.string().uuid('User is required.'),
    serviceRoleId: z.string().uuid('Service role is required.'),
    teamLead: z.boolean(),
    effectiveFrom: isoDateString('Required'),
    effectiveTo: isoDateString().or(z.literal('')),
  })
  .refine((v) => v.effectiveTo === '' || v.effectiveTo > v.effectiveFrom, {
    message: 'End must be after start',
    path: ['effectiveTo'],
  });
type FormValues = z.infer<typeof FormSchema>;

const FIELD_NAMES = [
  'userId',
  'serviceRoleId',
  'teamLead',
  'effectiveFrom',
  'effectiveTo',
] as const;
type FieldName = (typeof FIELD_NAMES)[number];
const isFormField = (s: string): s is FieldName => (FIELD_NAMES as readonly string[]).includes(s);

/**
 * True when moving the start date from `from` to `to` lands in a different
 * calendar month (or year). ISO dates are `YYYY-MM-DD`, so the `YYYY-MM`
 * prefix is the month key. A cross-month shift can move the membership into
 * or out of an already-reported period, so we confirm before saving.
 */
const crossesMonthBoundary = (from: string, to: string): boolean =>
  from.slice(0, 7) !== to.slice(0, 7);

export function CustomerTeamMemberFormSheet({ open, onOpenChange, customerId, member }: Props) {
  const queryClient = useQueryClient();
  const isEdit = member != null;

  const form = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      userId: '',
      serviceRoleId: '',
      teamLead: false,
      effectiveFrom: localTodayIso(),
      effectiveTo: '',
    },
  });

  // Reset whenever the sheet opens or the target member changes.
  useEffect(() => {
    if (!open) return;
    if (member) {
      form.reset({
        userId: member.userId,
        serviceRoleId: member.serviceRoleId,
        teamLead: member.teamLead,
        effectiveFrom: member.effectiveFrom,
        effectiveTo: member.effectiveTo ?? '',
      });
    } else {
      form.reset({
        userId: '',
        serviceRoleId: '',
        teamLead: false,
        effectiveFrom: localTodayIso(),
        effectiveTo: '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, member?.id, member?.updatedAt]);

  const mutation = useMutation({
    mutationFn: async (v: FormValues) => {
      if (isEdit && member) {
        // userId is immutable on patch (server rejects); move semantics happen
        // via end + create. effectiveFrom IS editable — the start date can be
        // corrected in place; the server re-checks the range and overlap.
        const payload: PatchCustomerTeamMemberInput = {
          serviceRoleId: v.serviceRoleId,
          teamLead: v.teamLead,
          effectiveFrom: v.effectiveFrom,
          effectiveTo: v.effectiveTo === '' ? null : v.effectiveTo,
        };
        return customerTeamApi.patch(member.id, payload);
      }
      const payload: CreateCustomerTeamMemberInput = {
        userId: v.userId,
        serviceRoleId: v.serviceRoleId,
        teamLead: v.teamLead,
        effectiveFrom: v.effectiveFrom,
        effectiveTo: v.effectiveTo === '' ? null : v.effectiveTo,
      };
      return customerTeamApi.create(customerId, payload);
    },
    onSuccess: () => {
      toast.success(isEdit ? 'Team member updated' : 'Team member added');
      queryClient.invalidateQueries({ queryKey: queryKeys.customerTeam.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.audit.all });
      onOpenChange(false);
    },
    onError: (err) => {
      if (err instanceof ApiError && err.fields?.length) {
        for (const fe of err.fields) {
          if (isFormField(fe.path)) {
            form.setError(fe.path, { message: fe.message });
          }
        }
        toast.error('Please fix the highlighted fields');
        return;
      }
      toast.error(err instanceof Error ? err.message : 'Save failed');
    },
  });

  // When editing shifts the start date across a month boundary, hold the
  // validated values here and confirm before saving instead of mutating.
  const [pendingCrossMonth, setPendingCrossMonth] = useState<FormValues | null>(null);

  const onSubmit = form.handleSubmit((v) => {
    if (
      isEdit &&
      member &&
      v.effectiveFrom !== member.effectiveFrom &&
      crossesMonthBoundary(member.effectiveFrom, v.effectiveFrom)
    ) {
      setPendingCrossMonth(v);
      return;
    }
    mutation.mutate(v);
  });
  const errors = form.formState.errors;

  const { requestOpenChange, discardDialog } = useUnsavedChangesGuard({
    open,
    isDirty: form.formState.isDirty,
    isSubmitting: mutation.isPending,
    onOpenChange,
  });

  return (
    <>
      <Sheet open={open} onOpenChange={requestOpenChange}>
        <SheetContent
          className="flex flex-col gap-0 sm:max-w-lg"
          // Don't autofocus the first field on open. On edit the User picker is
          // disabled, so focus would otherwise land on the Service role
          // combobox — which opens its dropdown on focus, covering the form and
          // making the other fields hard to reach. Let the user click in.
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <SheetHeader>
            <SheetTitle>{isEdit ? 'Edit team member' : 'Add team member'}</SheetTitle>
            <SheetDescription>
              {isEdit
                ? 'Update an existing membership. Saves overwrite in place.'
                : 'Assign a user to this customer’s team for an effective period.'}
            </SheetDescription>
          </SheetHeader>

          <form onSubmit={onSubmit} className="flex min-h-0 flex-1 flex-col">
            <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-4 py-3">
              <div className="space-y-1.5">
                <Label htmlFor="cf-user">User</Label>
                <UserAutocomplete
                  value={form.watch('userId') || null}
                  onChange={(u) =>
                    form.setValue('userId', u?.id ?? '', {
                      shouldValidate: true,
                      shouldDirty: true,
                    })
                  }
                  // A user may serve multiple roles on one customer, so the
                  // add path no longer excludes users already on the team —
                  // re-picking the same user adds another role. The server
                  // 409s only on a duplicate (user, role).
                  disabled={isEdit}
                  hasError={!!errors.userId}
                />
                {errors.userId ? (
                  <p className="text-xs text-destructive">{errors.userId.message}</p>
                ) : null}
                {isEdit ? (
                  <p className="text-xs text-muted-foreground">
                    Locked on edit — end this membership and add a new one to move it to a different
                    user.
                  </p>
                ) : null}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="cf-role">Service role</Label>
                <ServiceRoleAutocomplete
                  id="cf-role"
                  value={form.watch('serviceRoleId') || null}
                  onChange={(r) =>
                    form.setValue('serviceRoleId', r?.id ?? '', {
                      shouldValidate: true,
                      shouldDirty: true,
                    })
                  }
                  hasError={!!errors.serviceRoleId}
                />
                {errors.serviceRoleId ? (
                  <p className="text-xs text-destructive">{errors.serviceRoleId.message}</p>
                ) : null}
              </div>

              <div className="space-y-1.5">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="size-4 rounded border-input"
                    checked={form.watch('teamLead')}
                    onChange={(e) =>
                      form.setValue('teamLead', e.target.checked, { shouldDirty: true })
                    }
                  />
                  <span>Team lead</span>
                </label>
                {/* Server enforces one current team lead per customer; surface that
                    409 inline under this control rather than only as a toast. */}
                {errors.teamLead ? (
                  <p className="text-xs text-destructive">{errors.teamLead.message}</p>
                ) : null}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="cf-from">Effective from</Label>
                  <DatePicker
                    id="cf-from"
                    value={form.watch('effectiveFrom')}
                    onChange={(v) =>
                      form.setValue('effectiveFrom', v, {
                        shouldValidate: true,
                        shouldDirty: true,
                      })
                    }
                    ariaInvalid={!!errors.effectiveFrom}
                  />
                  {errors.effectiveFrom ? (
                    <p className="text-xs text-destructive">{errors.effectiveFrom.message}</p>
                  ) : null}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="cf-to">Effective to (optional)</Label>
                  <DatePicker
                    id="cf-to"
                    value={form.watch('effectiveTo')}
                    onChange={(v) =>
                      form.setValue('effectiveTo', v, { shouldValidate: true, shouldDirty: true })
                    }
                    clearable
                    ariaInvalid={!!errors.effectiveTo}
                  />
                  {errors.effectiveTo ? (
                    <p className="text-xs text-destructive">{errors.effectiveTo.message}</p>
                  ) : null}
                </div>
              </div>
            </div>

            <SheetFooter className="flex-row justify-end border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => requestOpenChange(false)}
                disabled={mutation.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Add member'}
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>
      {discardDialog}
      <ConfirmDialog
        open={pendingCrossMonth != null}
        title="Change the start date?"
        confirmLabel="Save changes"
        description={
          member ? (
            <>
              This moves the effective-from date from{' '}
              <span className="font-medium text-foreground">{member.effectiveFrom}</span> to{' '}
              <span className="font-medium text-foreground">
                {pendingCrossMonth?.effectiveFrom}
              </span>
              , crossing a month boundary. This can affect periods that may already be reported.
              Save anyway?
            </>
          ) : null
        }
        onConfirm={() => {
          const v = pendingCrossMonth;
          setPendingCrossMonth(null);
          if (v) mutation.mutate(v);
        }}
        onCancel={() => setPendingCrossMonth(null)}
      />
    </>
  );
}
